/**
 * Meeting Transcription crawl — client-only input rules.
 *
 * Retention (FE): transcript files live only in component state, are never
 * written to localStorage or Redux, and are cleared after a successful upload
 * or unmount.
 *
 * File uploads are capped at 25 MB. `TRANSCRIPT_INPUT_MAX_CHARS` is the UTF-8
 * worst-case character ceiling for that byte cap.
 */
export const TRANSCRIPT_INPUT_MAX_FILE_BYTES = 25 * 1024 * 1024;

/** UTF-8 worst case (4 bytes/char) for the 25 MB file cap. */
export const TRANSCRIPT_INPUT_MAX_CHARS = Math.floor(TRANSCRIPT_INPUT_MAX_FILE_BYTES / 4);

export const TRANSCRIPT_DATE_DISPLAY_FORMAT = 'MM/DD/YYYY' as const;
export const TRANSCRIPT_DATE_API_FORMAT = 'YYYY-MM-DD' as const;

export const TRANSCRIPT_FILE_EXTENSIONS = ['.vtt', '.txt', '.docx'] as const;

export const TRANSCRIPT_FILE_MIME_TYPES = new Set([
    'text/vtt',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]);

export const TRANSCRIPT_FILE_INPUT_ACCEPT = [
    ...TRANSCRIPT_FILE_EXTENSIONS,
    'text/vtt',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
].join(',');

export const getTranscriptFileOverLimitMessage = (maxBytes: number = TRANSCRIPT_INPUT_MAX_FILE_BYTES): string =>
    `File exceeds the ${(maxBytes / (1024 * 1024)).toLocaleString('en-US', { maximumFractionDigits: 1 })} MB size limit. Choose a smaller file and try again.`;

export const TRANSCRIPT_EMPTY_MESSAGE = 'Transcript is empty.';
export const TRANSCRIPT_FILE_EMPTY_MESSAGE = 'The selected file is empty.';
export const TRANSCRIPT_FILE_TYPE_MESSAGE = 'Upload a .vtt, .txt, or .docx file.';
export const TRANSCRIPT_HOSPITAL_REQUIRED_MESSAGE = 'Hospital is required.';
export const TRANSCRIPT_DATE_REQUIRED_MESSAGE = 'Meeting date is required.';
export const TRANSCRIPT_DATE_INVALID_MESSAGE = 'Meeting date must be a valid date.';
export const TRANSCRIPT_TYPE_REQUIRED_MESSAGE = 'Meeting type is required.';

export const STUB_MATCH_SESSION_ID = 'stub-session';

/** Crawl default until product sets a backend timeout. */
export const MATCH_VERIFY_TIMEOUT_MS = 24 * 60 * 60 * 1000;

export const MATCH_NAV_BUTTON_LABEL = 'Verify patient matches';
export const HISTORY_EMPTY_MESSAGE = 'No meeting transcriptions for this hospital.';
export const HISTORY_SELECT_HOSPITAL_MESSAGE = 'Select a hospital to view meeting transcriptions.';
export const HISTORY_VERIFY_LABEL = 'Verify';
export const HISTORY_DOWNLOAD_PDF_LABEL = 'Download PDF';
export const UPLOAD_NAV_BUTTON_LABEL = 'Upload transcript';
export const UPLOAD_PAGE_TITLE = 'Upload transcript';

export const FAILED_JOB_SECTION_TITLE = 'Meeting transcription failed';
export const FAILED_JOB_CANCEL_LABEL = 'Cancel';
export const FAILED_JOB_RETRY_LABEL = 'Retry';

export const MATCH_PARTIAL_WARNING =
    'You can confirm some matches and continue. Patients you do not verify will not have next steps appear on the patient census. Unverified and not-in-census patients will still be included in the emailed PDF.';

export const MATCH_PROCEED_SUCCESS_MESSAGE =
    'Next-step generation will continue in the background. You will be notified by email. No next step was written for unverified patients.';

export const MATCH_EMPTY_EXTRACTED_MESSAGE = 'No patients were extracted from this transcript.';

export const MATCH_TIMEOUT_PROCEED_MESSAGE =
    'Session expired. Confirmed matches will proceed. Unverified patients were flagged to the emailed PDF.';

export const MATCH_NOT_IN_CENSUS_LABEL = 'Not in census';
export const MATCH_SELECT_PATIENT_LABEL = 'Select patient';
export const MATCH_SELECT_SOURCE_AI = 'ai';
export const MATCH_SELECT_SOURCE_DROPDOWN = 'dropdown';
export const MATCH_SELECT_SOURCE_AI_LABEL = 'AI suggested matches';
export const MATCH_SELECT_SOURCE_DROPDOWN_LABEL = 'Select from dropdown';
export const MATCH_SELECT_CENSUS_PLACEHOLDER = 'Search by name, FIN, or room';
export const MATCH_CENSUS_SEARCH_LIMIT = 25;
export const MATCH_CENSUS_SEARCH_DEBOUNCE_MS = 300;
export const MATCH_STATUS_CONFIRMED = 'Confirmed';
export const MATCH_STATUS_UNCERTAIN = 'Uncertain';
export const MATCH_STATUS_HIGH_CONFIDENCE = 'High confidence';

export const getMatchPartialDialogMessage = (unverifiedCount: number): string =>
    `You have ${unverifiedCount} unverified patient${unverifiedCount === 1 ? '' : 's'}. Their next steps will not appear on the patient census. They will be carried to the emailed PDF. Continue?`;
